import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { demoContext, getBackend } from '#backend';
import type { DemoPack, IssueRecord, IssueStatus } from '@demopack/demopack';

const PAGE_SIZE = 5;

type DemoWindow = typeof window & {
  __demoIssueBoard?: DemoPack;
};

type SortKey = 'id' | 'priority-desc' | 'priority-asc' | 'title';

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: 'Open',
  'in-progress': 'In progress',
  done: 'Done',
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status as IssueStatus] ?? status;
}

export function App(): JSX.Element {
  const [issues, setIssues] = useState<IssueRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IssueRecord | null>(null);
  const [lifecycle, setLifecycle] = useState('initializing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [demo, setDemo] = useState<DemoPack | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | IssueStatus>('all');
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [page, setPage] = useState(0);
  const [createTitle, setCreateTitle] = useState('');
  const [createStatus, setCreateStatus] = useState<IssueStatus>('open');
  const [createDescription, setCreateDescription] = useState('');
  const [createPriority, setCreatePriority] = useState('0');
  const [editTitle, setEditTitle] = useState('');
  const [editStatus, setEditStatus] = useState<IssueStatus>('open');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('0');
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refreshList = useCallback(
    async (instance: DemoPack, overrides: { search?: string; status?: 'all' | IssueStatus; sort?: SortKey; page?: number } = {}) => {
      const querySearch = overrides.search ?? appliedSearch;
      const queryStatus = overrides.status ?? statusFilter;
      const querySort = overrides.sort ?? sortKey;
      const queryPage = overrides.page ?? page;

      let request = instance.supabase.from('issues').select('*', { count: 'exact' });
      if (querySearch.trim()) {
        request = request.ilike('title', `*${querySearch.trim()}*`);
      }
      if (queryStatus !== 'all') {
        request = request.eq('status', queryStatus);
      }
      if (querySort === 'priority-desc') {
        request = request.order('priority', { ascending: false }).order('id', { ascending: true });
      } else if (querySort === 'priority-asc') {
        request = request.order('priority', { ascending: true }).order('id', { ascending: true });
      } else if (querySort === 'title') {
        request = request.order('title', { ascending: true });
      } else {
        request = request.order('id', { ascending: true });
      }

      const from = queryPage * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error, count } = await request.range(from, to);
      if (error) {
        setErrorMessage(error.message);
        setIssues([]);
        return;
      }
      setIssues((data ?? []) as IssueRecord[]);
      setTotal(count ?? 0);
      setErrorMessage(null);
    },
    [appliedSearch, page, sortKey, statusFilter],
  );

  const refreshListRef = useRef(refreshList);
  refreshListRef.current = refreshList;

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    const demoWindow = window as DemoWindow;

    const init = async (): Promise<void> => {
      try {
        const backend = await getBackend();
        if (cancelled) {
          return;
        }
        if (!backend.demo || !demoContext.isDemo) {
          setLifecycle('production');
          setErrorMessage('This build is production mode. DemoPack runtime and seed data are not included.');
          return;
        }
        demoWindow.__demoIssueBoard = backend.demo;
        setDemo(backend.demo);
        unsubscribe = backend.demo.subscribe((snapshot) => {
          if (cancelled) {
            return;
          }
          setLifecycle(snapshot.state);
          if (snapshot.error) {
            setErrorMessage(snapshot.error.message);
          }
          if (snapshot.state === 'ready') {
            setSelected(null);
            setEditing(false);
            setConfirmDelete(false);
            setPage(0);
            setAppliedSearch('');
            setSearch('');
            setStatusFilter('all');
            setSortKey('id');
            void refreshListRef.current(backend.demo as DemoPack, { search: '', status: 'all', sort: 'id', page: 0 });
          }
        });
      } catch (caught) {
        if (!cancelled) {
          setLifecycle('failed');
          setErrorMessage(caught instanceof Error ? caught.message : 'Unable to initialize the demo.');
          setDemo(null);
        }
      }
    };

    void init();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const runBusy = async (work: () => Promise<void>): Promise<void> => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  const onSearch = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!demo) {
      return;
    }
    setAppliedSearch(search);
    setPage(0);
    await refreshList(demo, { search, page: 0 });
  };

  const onFilter = async (next: 'all' | IssueStatus): Promise<void> => {
    if (!demo) {
      return;
    }
    setStatusFilter(next);
    setPage(0);
    await refreshList(demo, { status: next, page: 0 });
  };

  const onSort = async (next: SortKey): Promise<void> => {
    if (!demo) {
      return;
    }
    setSortKey(next);
    setPage(0);
    await refreshList(demo, { sort: next, page: 0 });
  };

  const onPage = async (next: number): Promise<void> => {
    if (!demo) {
      return;
    }
    setPage(next);
    await refreshList(demo, { page: next });
  };

  const openIssue = async (id: number): Promise<void> => {
    if (!demo) {
      return;
    }
    const { data, error } = await demo.supabase.from('issues').select('*').eq('id', id).single();
    if (error || !data) {
      setErrorMessage(error?.message ?? 'Issue could not be loaded.');
      return;
    }
    const issue = data as IssueRecord;
    setSelected(issue);
    setEditTitle(issue.title);
    setEditStatus(issue.status);
    setEditDescription(issue.description ?? '');
    setEditPriority(String(issue.priority));
    setEditing(false);
    setConfirmDelete(false);
    setFormError(null);
  };

  const onCreate = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!demo || lifecycle !== 'ready') {
      return;
    }
    const title = createTitle.trim();
    if (!title) {
      setFormError('Title is required.');
      return;
    }
    const priority = Number(createPriority);
    if (!Number.isInteger(priority)) {
      setFormError('Priority must be a whole number.');
      return;
    }
    await runBusy(async () => {
      const { data, error } = await demo.supabase
        .from('issues')
        .insert({
          title,
          status: createStatus,
          description: createDescription.trim() || null,
          priority,
        })
        .select()
        .single();
      if (error) {
        setFormError(error.message);
        return;
      }
      setFormError(null);
      setCreateTitle('');
      setCreateDescription('');
      setCreatePriority('0');
      setCreateStatus('open');
      await refreshList(demo);
      if (data) {
        setSelected(data as IssueRecord);
      }
    });
  };

  const onSave = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!demo || !selected) {
      return;
    }
    const title = editTitle.trim();
    if (!title) {
      setFormError('Title is required.');
      return;
    }
    const priority = Number(editPriority);
    if (!Number.isInteger(priority)) {
      setFormError('Priority must be a whole number.');
      return;
    }
    await runBusy(async () => {
      const { data, error } = await demo.supabase
        .from('issues')
        .update({
          title,
          status: editStatus,
          description: editDescription.trim() || null,
          priority,
        })
        .eq('id', selected.id)
        .select()
        .single();
      if (error) {
        setFormError(error.message);
        return;
      }
      setFormError(null);
      setEditing(false);
      setSelected(data as IssueRecord);
      await refreshList(demo);
    });
  };

  const onDelete = async (): Promise<void> => {
    if (!demo || !selected) {
      return;
    }
    await runBusy(async () => {
      const { error } = await demo.supabase.from('issues').delete().eq('id', selected.id);
      if (error) {
        setFormError(error.message);
        return;
      }
      setSelected(null);
      setConfirmDelete(false);
      await refreshList(demo);
    });
  };

  const onReset = async (): Promise<void> => {
    if (!demo) {
      return;
    }
    await runBusy(async () => {
      await demoContext.reset();
    });
  };

  const onRecover = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('seed');
    window.location.replace(url.toString());
  };

  const rangeLabel = useMemo(() => {
    if (total === 0) {
      return 'No issues match the current filters.';
    }
    const start = page * PAGE_SIZE + 1;
    const end = Math.min(total, (page + 1) * PAGE_SIZE);
    return `Showing ${start}–${end} of ${total}`;
  }, [page, total]);

  const ready = lifecycle === 'ready' && demo !== null;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#issue-list">
        Skip to issue list
      </a>
      <header className="demo-bar">
        <div className="demo-bar-copy">
          <p>
            <span className="demo-label">Demo</span>
            Changes stay in this tab and reset on refresh.
          </p>
          <p className="demo-user">
            {demo
              ? `Demo identity: ${demo.user.displayName} (${demo.user.email}). Fictional fixture, not authentication or RLS.`
              : 'Demo identity is supplied after the local engine starts.'}
          </p>
          <p className="demo-state" role="status">
            {lifecycle === 'initializing' ? 'Initializing demo…' : null}
            {lifecycle === 'resetting' ? 'Resetting demo…' : null}
            {lifecycle === 'ready' ? 'Demo ready.' : null}
            {lifecycle === 'failed' ? 'Demo failed to start.' : null}
            {lifecycle === 'production' ? 'Production build (no demo engine).' : null}
          </p>
        </div>
        <div className="demo-bar-actions">
          {ready ? (
            <button type="button" onClick={() => void onReset()} disabled={busy}>
              Reset demo
            </button>
          ) : null}
          <a href="#how-it-works">How this demo works</a>
          <span className="doc-path">
            Integration notes: <code>docs/INTEGRATION.md</code> in this repository. No published package URL.
          </span>
        </div>
      </header>

      <main className="board-shell" data-state={lifecycle}>
        <h1>Issue Board</h1>
        <p className="lede">
          A fictional tracker served as static files. Every list, search, and edit goes through the real Supabase
          JavaScript client and DemoPack&apos;s local engine.
        </p>

        <section id="how-it-works" className="guide" aria-labelledby="guide-heading">
          <h2 id="guide-heading">Try the isolation</h2>
          <ol>
            <li>Create an issue on this board. It appears in search on this tab only.</li>
            <li>Open this same URL in another tab. That tab still shows the original seed.</li>
            <li>Press Reset demo. This tab returns to the seed; the other tab is unchanged until you refresh it.</li>
          </ol>
        </section>

        {lifecycle === 'initializing' ? <p>Loading demo data…</p> : null}

        {lifecycle === 'failed' ? (
          <div role="alert" className="banner error">
            <p>{errorMessage ?? 'Unable to initialize the demo.'}</p>
            <button type="button" onClick={onRecover}>
              Retry with valid seed
            </button>
          </div>
        ) : null}

        {ready ? (
          <>
            {errorMessage ? (
              <p role="alert" className="banner error">
                {errorMessage}
              </p>
            ) : null}

            <section className="toolbar" aria-label="Issue filters">
              <form className="search-form" onSubmit={(event) => void onSearch(event)}>
                <label htmlFor="issue-search">Search titles</label>
                <input
                  id="issue-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  disabled={busy}
                  placeholder="onboarding"
                />
                <button type="submit" disabled={busy}>
                  Search
                </button>
              </form>
              <div className="toolbar-controls">
                <label htmlFor="status-filter">Status</label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  disabled={busy}
                  onChange={(event) => void onFilter(event.target.value as 'all' | IssueStatus)}
                >
                  <option value="all">All statuses</option>
                  <option value="open">Open</option>
                  <option value="in-progress">In progress</option>
                  <option value="done">Done</option>
                </select>
                <label htmlFor="issue-sort">Sort</label>
                <select
                  id="issue-sort"
                  value={sortKey}
                  disabled={busy}
                  onChange={(event) => void onSort(event.target.value as SortKey)}
                >
                  <option value="id">Created order</option>
                  <option value="priority-desc">Priority, high to low</option>
                  <option value="priority-asc">Priority, low to high</option>
                  <option value="title">Title A–Z</option>
                </select>
              </div>
            </section>

            <div className="board-grid">
              <section className="list-panel">
                <div className="list-header">
                  <h2 id="issue-list">Issues</h2>
                  <p className="range-label">{rangeLabel}</p>
                </div>
                <ul className="issue-list" aria-labelledby="issue-list">
                  {issues.map((issue) => (
                    <li key={issue.id}>
                      <button
                        type="button"
                        className={selected?.id === issue.id ? 'issue-row active' : 'issue-row'}
                        onClick={() => void openIssue(issue.id)}
                        disabled={busy}
                      >
                        <span className="issue-title">{issue.title}</span>
                        <span className={`status-pill status-${issue.status}`}>
                          <span className="status-mark" aria-hidden="true">
                            {issue.status === 'open' ? '○' : issue.status === 'in-progress' ? '◐' : '●'}
                          </span>
                          {statusLabel(issue.status)}
                          <span className="priority">Priority {issue.priority}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="pagination">
                  <button type="button" onClick={() => void onPage(page - 1)} disabled={busy || page === 0}>
                    Previous page
                  </button>
                  <p>
                    Page {page + 1} of {pageCount}
                  </p>
                  <button
                    type="button"
                    onClick={() => void onPage(page + 1)}
                    disabled={busy || page + 1 >= pageCount}
                  >
                    Next page
                  </button>
                </div>
              </section>

              <section className="detail-panel" aria-live="polite">
                {selected ? (
                  <>
                    <h2>Issue {selected.id}</h2>
                    {!editing ? (
                      <article>
                        <h3>{selected.title}</h3>
                        <p>
                          <span className={`status-pill status-${selected.status}`}>
                            <span className="status-mark" aria-hidden="true">
                              {selected.status === 'open' ? '○' : selected.status === 'in-progress' ? '◐' : '●'}
                            </span>
                            {statusLabel(selected.status)}
                          </span>{' '}
                          Priority {selected.priority}
                        </p>
                        <p>{selected.description ?? 'No description.'}</p>
                        <div className="detail-actions">
                          <button type="button" onClick={() => setEditing(true)} disabled={busy}>
                            Edit issue
                          </button>
                          {confirmDelete ? (
                            <>
                              <p role="alert">Delete this issue from this tab&apos;s demo data?</p>
                              <button type="button" onClick={() => void onDelete()} disabled={busy}>
                                Confirm delete
                              </button>
                              <button type="button" onClick={() => setConfirmDelete(false)} disabled={busy}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button type="button" className="danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
                              Delete issue
                            </button>
                          )}
                        </div>
                      </article>
                    ) : (
                      <form className="issue-form stacked" onSubmit={(event) => void onSave(event)}>
                        <IssueFields
                          title={editTitle}
                          status={editStatus}
                          description={editDescription}
                          priority={editPriority}
                          disabled={busy}
                          onTitle={setEditTitle}
                          onStatus={setEditStatus}
                          onDescription={setEditDescription}
                          onPriority={setEditPriority}
                        />
                        {formError ? <p role="alert">{formError}</p> : null}
                        <div className="detail-actions">
                          <button type="submit" disabled={busy}>
                            Save changes
                          </button>
                          <button type="button" onClick={() => setEditing(false)} disabled={busy}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                ) : (
                  <p>Select an issue to read, edit, or delete it. New issues also open here after they are created.</p>
                )}
              </section>
            </div>

            <section className="create-panel" aria-labelledby="create-heading">
              <h2 id="create-heading">Create issue</h2>
              <form className="issue-form stacked" onSubmit={(event) => void onCreate(event)}>
                <IssueFields
                  title={createTitle}
                  status={createStatus}
                  description={createDescription}
                  priority={createPriority}
                  disabled={busy}
                  onTitle={setCreateTitle}
                  onStatus={setCreateStatus}
                  onDescription={setCreateDescription}
                  onPriority={setCreatePriority}
                  create
                />
                {formError && !editing ? <p role="alert">{formError}</p> : null}
                <button type="submit" disabled={busy || editing}>
                  Add issue
                </button>
              </form>
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}

function IssueFields(props: {
  title: string;
  status: IssueStatus;
  description: string;
  priority: string;
  disabled: boolean;
  create?: boolean;
  onTitle: (value: string) => void;
  onStatus: (value: IssueStatus) => void;
  onDescription: (value: string) => void;
  onPriority: (value: string) => void;
}): JSX.Element {
  const titleId = props.create ? 'issue-title' : 'edit-issue-title';
  return (
    <>
      <label htmlFor={titleId}>Issue title</label>
      <input
        id={titleId}
        name="title"
        aria-label="Issue title"
        value={props.title}
        onChange={(event) => props.onTitle(event.target.value)}
        disabled={props.disabled}
        required
      />
      <label htmlFor={`${titleId}-status`}>Status</label>
      <select
        id={`${titleId}-status`}
        value={props.status}
        disabled={props.disabled}
        onChange={(event) => props.onStatus(event.target.value as IssueStatus)}
      >
        <option value="open">Open</option>
        <option value="in-progress">In progress</option>
        <option value="done">Done</option>
      </select>
      <label htmlFor={`${titleId}-priority`}>Priority</label>
      <input
        id={`${titleId}-priority`}
        type="text"
        inputMode="numeric"
        value={props.priority}
        disabled={props.disabled}
        onChange={(event) => props.onPriority(event.target.value)}
      />
      <label htmlFor={`${titleId}-description`}>Description</label>
      <textarea
        id={`${titleId}-description`}
        value={props.description}
        disabled={props.disabled}
        onChange={(event) => props.onDescription(event.target.value)}
        rows={3}
      />
    </>
  );
}
